import BaseProduct from './BaseProduct';
import { ProductId } from '../types';
import store, { getLayerOpacity } from '../state';
import ManifestTimelineHelper from '../services/ManifestTimelineHelper';
import maplibregl from 'maplibre-gl';
import { getTileUrl, buildPmtilesUrl } from '../config';

export class HrtempContoursLayer extends BaseProduct {
  public readonly id: ProductId = 'hrtemp_contours';
  private vectorSourceId = 'hrtemp-contours-source';
  private contourLayerId = 'hrtemp-contour-line';
  private labelLayerId = 'hrtemp-contour-label';
  private currentUrl = '';
  private currentSpacing: 'low' | 'high' = 'low';

  constructor(map: maplibregl.Map, layerManager: any) {
    super(map, layerManager);
    store.subscribe((state) => {
      if (!this.active) return;
      const spacing = state.contourSpacing || 'low';
      if (spacing !== this.currentSpacing) {
        this.currentSpacing = spacing;
        this.refresh();
      }
    });
  }

  protected onActivate(): void {
    this.refresh();
  }

  protected onDeactivate(): void {
    this.removeContourLayers();
  }

  protected onTimeUpdate(_date: string, _hour: string): void {
    this.refresh();
  }

  private removeContourLayers() {
    if (this.map.getLayer(this.labelLayerId)) this.map.removeLayer(this.labelLayerId);
    if (this.map.getLayer(this.contourLayerId)) this.map.removeLayer(this.contourLayerId);
    if (this.map.getSource(this.vectorSourceId)) this.map.removeSource(this.vectorSourceId);
    this.currentUrl = '';
  }

  public refresh() {
    if (!this.active) return;
    const state = store.getState();
    const manifest = state.manifest;
    if (!manifest) return;
    const opacity = getLayerOpacity(this.id, 1.0);
    const current = state.currentTimeStep;
    if (!current) return;

    // Prioritize SKYSKOUT HRes Topo for high-resolution temperature contours
    const rawModel = state.selectedModels[this.id];
    let model = rawModel || 'skysk_hres_topo';
    let clampedFrame = manifest ? ManifestTimelineHelper.getClampedFrame(manifest, 'hrtemp_contours', model, current) : null;
    if (!clampedFrame && manifest) {
      clampedFrame = ManifestTimelineHelper.getClampedFrame(manifest, 'hrtemp', model, current);
    }

    if (!clampedFrame && manifest) {
      const avail = ManifestTimelineHelper.getAvailableModelsForProduct(manifest, 'hrtemp_contours');
      for (const mObj of avail) {
        const frame = ManifestTimelineHelper.getClampedFrame(manifest, 'hrtemp_contours', mObj.id, current);
        if (frame) {
          model = mObj.id;
          clampedFrame = frame;
          break;
        }
      }
    }

    if (!clampedFrame) {
      this.removeContourLayers();
      return;
    }

    const dateStr = clampedFrame.dateStr;
    const hourStr = clampedFrame.hourStr.replace(/^Z/, '');

    // Resolve authoritative Vector PMTiles URL
    const rawUrl = getTileUrl(manifest, this.id, model, dateStr, hourStr);
    const vectorUrl = buildPmtilesUrl(rawUrl);

    // Temperature color interpolation across Fahrenheit values
    const tempColorExp: maplibregl.ExpressionSpecification = [
      'interpolate',
      ['linear'],
      ['get', 'temp_f'],
      0,  '#7c3aed', // <= 0°F: deep purple
      15, '#2563eb', // 15°F: navy blue
      25, '#0ea5e9', // 25°F: light blue
      32, '#00f0ff', // 32°F: freezing cyan
      40, '#10b981', // 40°F: emerald green
      50, '#22c55e', // 50°F: bright green
      60, '#eab308', // 60°F: warm gold
      70, '#f97316', // 70°F: orange
      80, '#ef4444', // 80°F: bright red
      95, '#b91c1c'  // 95°F+: crimson
    ];

    // Dynamic line width with freezing accent and zoom scaling
    const lineWidthExp: maplibregl.ExpressionSpecification = [
      'case',
      ['to-boolean', ['get', 'is_freezing']], 2.8,
      ['to-boolean', ['get', 'is_major']], 1.8,
      1.1
    ];

    // Filter: Zoom 0-4 shows continental tier; Zoom 5+ shows topographic tier
    const tierFilter: maplibregl.ExpressionSpecification = [
      'any',
      ['all', ['<', ['zoom'], 5], ['==', ['get', 'tier'], 'continental']],
      ['all', ['>=', ['zoom'], 5], ['==', ['get', 'tier'], 'topographic']]
    ];

    // Dynamic client-side label expression (°F / °C)
    const isCelsius = state.units === 'metric' || (state as any).temperatureUnit === 'c' || (state as any).tempUnit === 'c';
    const labelTextField: maplibregl.ExpressionSpecification = isCelsius ? [
      'case',
      ['to-boolean', ['get', 'is_freezing']], '0°C FREEZE',
      ['concat', ['to-string', ['get', 'temp_c']], '°C']
    ] : [
      'case',
      ['to-boolean', ['get', 'is_freezing']], '32°F FREEZE',
      ['concat', ['to-string', ['get', 'temp_f']], '°F']
    ];

    const symbolSpacing = state.contourSpacing === 'high' ? 450 : 220;

    const source = this.map.getSource(this.vectorSourceId) as any;
    if (source) {
      if (this.currentUrl !== vectorUrl) {
        this.currentUrl = vectorUrl;
        if (typeof source.setUrl === 'function') {
          source.setUrl(vectorUrl);
        } else {
          // Re-create source if setUrl is not supported
          this.removeContourLayers();
          this.refresh();
          return;
        }
      }

      if (this.map.getLayer(this.contourLayerId)) {
        this.map.setPaintProperty(this.contourLayerId, 'line-opacity', 0.92 * opacity);
      }
      if (this.map.getLayer(this.labelLayerId)) {
        this.map.setLayoutProperty(this.labelLayerId, 'text-field', labelTextField);
        this.map.setLayoutProperty(this.labelLayerId, 'symbol-spacing', symbolSpacing);
        this.map.setPaintProperty(this.labelLayerId, 'text-opacity', 0.95 * opacity);
      }
    } else {
      this.currentUrl = vectorUrl;
      this.map.addSource(this.vectorSourceId, {
        type: 'vector',
        url: vectorUrl,
        minzoom: 0,
        maxzoom: 8
      });

      this.map.addLayer({
        id: this.contourLayerId,
        type: 'line',
        source: this.vectorSourceId,
        'source-layer': 'contours',
        filter: tierFilter,
        paint: {
          'line-color': tempColorExp,
          'line-width': lineWidthExp,
          'line-opacity': 0.92 * opacity
        }
      });

      this.map.addLayer({
        id: this.labelLayerId,
        type: 'symbol',
        source: this.vectorSourceId,
        'source-layer': 'contours',
        filter: tierFilter,
        layout: {
          'text-field': labelTextField,
          'text-font': ['Noto Sans Regular'],
          'text-size': 13,
          'symbol-placement': 'line',
          'symbol-spacing': symbolSpacing,
          'text-max-angle': 38,
          'text-rotation-alignment': 'map',
          'text-pitch-alignment': 'viewport'
        },
        paint: {
          'text-color': tempColorExp,
          'text-halo-color': '#000000',
          'text-halo-width': 3.0,
          'text-opacity': 0.95 * opacity
        }
      });
    }
  }
}
export default HrtempContoursLayer;
